import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Database,
  DollarSign,
  ExternalLink,
  RefreshCw,
  Server,
  Shield,
  XCircle,
} from 'lucide-react';

interface OpsData {
  ok: boolean;
  ts: string;
  links: {
    health_all: string;
    health: string;
    health_self: string;
    health_env: string;
    mirror_status: string;
    ship_log: string;
    vercel_dashboard: string;
    github_actions: string;
  };
  health: {
    all: { ok: boolean; database?: boolean; [key: string]: unknown };
    root: { ok: boolean; [key: string]: unknown };
    self: { ok: boolean; [key: string]: unknown };
    env: { ok: boolean; [key: string]: unknown };
  };
  telemetry: {
    mirror: { ok?: boolean; error?: string; [key: string]: unknown };
    ship: { ok?: boolean; error?: string; [key: string]: unknown };
  };
  vercel: { enabled: boolean; found?: boolean; id?: string; url?: string; [key: string]: unknown };
  github: { repo?: string; sha?: string; author?: string; message?: string; html_url?: string; found?: boolean; error?: string; [key: string]: unknown };
  env: Record<string, boolean>;
  functions?: { total: number; budget: number; routes: { path: string; file: string }[] };
  reliability?: { idempotency_table: boolean };
  features?: { supabase: boolean; resend: boolean; admin: boolean };
  pricing?: { domain_prices_table: boolean; webhook_events_table: boolean };
  tables?: {
    idempotencyTable: boolean;
    pricesTable: boolean;
    webhookEventsTable: boolean;
    metricsCacheTable: boolean;
    priceOverridesTable?: boolean;
  };
}

interface StripeMetrics {
  enabled: boolean;
  cached?: boolean;
  mrr_cents?: number;
  active_lto_count?: number;
  failures_24h?: number;
  error?: string;
}

export default function Ops() {
  const [data, setData] = useState<OpsData | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [testingSentry, setTestingSentry] = useState(false);
  const [stripeMetrics, setStripeMetrics] = useState<StripeMetrics | null>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  useEffect(() => {
    fetchOpsData();
    fetchStripeMetrics();
  }, []);

  const fetchOpsData = async () => {
    try {
      const response = await fetch('/api/ops', { cache: 'no-store' });
      const json = await response.json();
      if (json.ok && json.data) {
        setData(json.data);
      } else {
        setError('Failed to load ops data');
      }
    } catch (e: any) {
      console.warn('API call failed, using mock data for development:', e);
      // Mock data for local development when API is unavailable
      const mockData: OpsData = {
        ok: true,
        ts: new Date().toISOString(),
        links: {
          health_all: '/api/health/all',
          health: '/api/health',
          health_self: '/api/health/self-test',
          health_env: '/api/health/env',
          mirror_status:
            'https://raw.githubusercontent.com/UCgr8/Publick-4-A.D/main/mirror/status.json',
          ship_log:
            'https://raw.githubusercontent.com/UCgr8/Publick-4-A.D/main/shiplog/ship-log.json',
          vercel_dashboard: 'https://vercel.com/dashboard',
          github_actions: 'https://github.com/UCgr8/attaindomains/actions',
        },
        health: {
          all: { ok: true, database: true },
          root: { ok: true },
          self: { ok: true },
          env: { ok: true },
        },
        telemetry: {
          mirror: { ok: false, error: 'Mock data' },
          ship: { ok: false, error: 'Mock data' },
        },
        vercel: { enabled: false, found: false },
        github: { repo: 'UCgr8/attaindomains', found: false },
        env: {
          SITE_URL: true,
          SUPABASE_URL: true,
          SUPABASE_SERVICE_ROLE_KEY: true,
          RESEND_API_KEY: true,
          RESEND_FROM: true,
          NEXT_PUBLIC_CLARITY_ID: false,
          SENTRY: true,
          POSTHOG: false,
          VERCEL_TOKEN: false,
          VERCEL_PROJECT_ID: false,
          GITHUB_REPO: false,
          GITHUB_TOKEN: false,
        },
      };
      setData(mockData);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeMetrics = async () => {
    try {
      const response = await fetch('/api/manage?action=get-stripe-metrics', {
        credentials: 'include',
      });
      const json = await response.json();
      if (json.ok && json.data) {
        setStripeMetrics(json.data);
      }
    } catch (e) {
      console.warn('Stripe metrics fetch failed:', e);
      setStripeMetrics({ enabled: false });
    }
  };

  const handleRefreshMetrics = async () => {
    setLoadingMetrics(true);
    try {
      const response = await fetch('/api/manage?action=refresh-stripe-metrics', {
        method: 'POST',
        credentials: 'include',
      });
      const json = await response.json();
      if (json.ok && json.data) {
        setStripeMetrics(json.data);
      }
    } catch (e) {
      console.error('Stripe metrics refresh failed:', e);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleTestSentry = () => {
    setTestingSentry(true);
    try {
      // Trigger a test error for Sentry
      Sentry.captureMessage('Test event from /ops dashboard', 'info');

      // Also trigger a test exception
      setTimeout(() => {
        try {
          throw new Error('Test error from /ops dashboard - please ignore');
        } catch (err) {
          Sentry.captureException(err);
          alert('Test event sent to Sentry! Check your Sentry dashboard.');
        }
        setTestingSentry(false);
      }, 500);
    } catch (error) {
      console.error('Sentry test failed:', error);
      setTestingSentry(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <h1 className="text-2xl font-bold">Operations Dashboard</h1>
        <p className="text-muted-foreground mt-2">Loading...</p>
      </div>
    );
  }

  const StatusIcon = ({ status }: { status: boolean }) =>
    status ? (
      <CheckCircle2 className="h-5 w-5 text-green-500" />
    ) : (
      <XCircle className="h-5 w-5 text-red-500" />
    );

  return (
    <>
      <Helmet>
        <title>Operations Dashboard - Attain Domains</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="container mx-auto py-8 space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="h-8 w-8" />
            Operations Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            Real-time system health and monitoring
          </p>
        </div>

        {/* System Status Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              System Status
            </CardTitle>
            <CardDescription>
              Last updated: {data?.ts ? new Date(data.ts).toLocaleString() : 'Unknown'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <StatusIcon status={data?.ok || false} />
              <span className="text-xl font-semibold">
                {data?.ok ? 'All Systems Operational' : 'System Degraded'}
              </span>
              {data?.ok ? (
                <Badge variant="default" className="bg-green-500">
                  HEALTHY
                </Badge>
              ) : (
                <Badge variant="destructive">DEGRADED</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {error && (
          <Card className="border-red-500 bg-red-50 dark:bg-red-900/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                Error
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            {/* Serverless Functions Budget */}
            {data?.functions && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5" />
                    Serverless Functions
                  </CardTitle>
                <CardDescription>
                  Count: {data.functions.total}/{data.functions.budget}
                  {data.reliability?.idempotency_table && (
                    <span className="ml-3 text-green-600 dark:text-green-400">
                      • Idempotency ✓
                    </span>
                  )}
                  <div className="mt-2 text-xs space-x-3">
                    <span className={data.features?.supabase ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                      {data.features?.supabase ? '✓' : '✗'} Supabase
                    </span>
                    <span className={data.features?.resend ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                      {data.features?.resend ? '✓' : '✗'} Resend
                    </span>
                    <span className={data.features?.admin ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                      {data.features?.admin ? '✓' : '✗'} Admin
                    </span>
                  </div>
                  {(data.pricing || data.tables) && (
                    <div className="mt-2 text-xs space-x-3">
                      <span className={(data.tables?.pricesTable ?? data.pricing?.domain_prices_table) ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                        {(data.tables?.pricesTable ?? data.pricing?.domain_prices_table) ? '✓' : '✗'} Prices Table
                      </span>
                      <span className={(data.tables?.webhookEventsTable ?? data.pricing?.webhook_events_table) ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                        {(data.tables?.webhookEventsTable ?? data.pricing?.webhook_events_table) ? '✓' : '✗'} Webhook Log
                      </span>
                      {typeof data.tables?.idempotencyTable === 'boolean' && (
                        <span className={data.tables.idempotencyTable ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                          {data.tables.idempotencyTable ? '✓' : '✗'} Idempotency
                        </span>
                      )}
                      {typeof data.tables?.metricsCacheTable === 'boolean' && (
                        <span className={data.tables.metricsCacheTable ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                          {data.tables.metricsCacheTable ? '✓' : '✗'} Metrics Cache
                        </span>
                      )}
                    </div>
                  )}
                </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-sm">
                    {data.functions.total <= data.functions.budget ? (
                      <span className="text-green-600 dark:text-green-400">Within budget</span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400">Over budget</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Health Checks Grid */}
            <div className="grid gap-4 md:grid-cols-3">
              {/* API Health */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    API Health
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Status</span>
                      <StatusIcon status={data.health?.all?.ok || false} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Endpoint</span>
                      <span className="text-sm font-mono">/api/health</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Database Health */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Database
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Status</span>
                      <StatusIcon status={data.health?.all?.database !== false} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Connection</span>
                      <span className="text-sm">
                        {data.health?.all?.database !== false ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Environment */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    Environment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Status</span>
                      <StatusIcon status={data.env?.SITE_URL || false} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Mode</span>
                      <Badge variant={data.env?.SITE_URL ? 'default' : 'secondary'}>
                        {data.env?.SITE_URL ? 'Production' : 'Development'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sentry Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Sentry Error Tracking
                </CardTitle>
                <CardDescription>
                  Monitor and debug production errors in real-time
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <StatusIcon status={data.env?.SENTRY || false} />
                  <span className="font-semibold">
                    {data.env?.SENTRY ? 'Enabled & Configured' : 'Disabled'}
                  </span>
                </div>

                {data.env?.SENTRY && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">DSN Status</span>
                        <Badge variant="default" className="bg-green-500">
                          Configured
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Environment</span>
                        <span className="font-mono">
                          {data.env?.SITE_URL ? 'production' : 'development'}
                        </span>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex gap-2">
                      <Button
                        onClick={handleTestSentry}
                        disabled={testingSentry}
                        variant="outline"
                        size="sm"
                      >
                        {testingSentry ? 'Sending Test Event...' : 'Send Test Event'}
                      </Button>
                      <a
                        href="https://domains-lb.sentry.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                      >
                        Open Dashboard
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Stripe Payments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Stripe Payments
                </CardTitle>
                <CardDescription>
                  Payment processing and billing integration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <StatusIcon status={data.env?.VITE_STRIPE_PUBLIC_KEY || false} />
                  <span className="font-semibold">
                    {data.env?.VITE_STRIPE_PUBLIC_KEY ? 'Configured' : 'Not Configured'}
                  </span>
                </div>

                {data.env?.VITE_STRIPE_PUBLIC_KEY && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">API Keys</span>
                        <Badge variant="default" className="bg-green-500">
                          Set
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Webhook Secret</span>
                        <Badge variant={data.env?.STRIPE_WEBHOOK_SECRET ? 'default' : 'secondary'}>
                          {data.env?.STRIPE_WEBHOOK_SECRET ? 'Set' : 'Missing'}
                        </Badge>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex gap-2">
                      <a
                        href="https://dashboard.stripe.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                      >
                        Stripe Dashboard
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                      <a
                        href="/docs/payments.md"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                      >
                        Docs
                      </a>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Stripe Metrics (P20251023-PAYMENTS-P5C) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Stripe Metrics
                </CardTitle>
                <CardDescription>
                  MRR, active subscriptions, and webhook health
                  {stripeMetrics?.cached && (
                    <span className="ml-2 text-xs text-muted-foreground">(Cached)</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!stripeMetrics?.enabled ? (
                  <div className="flex items-center gap-3">
                    <StatusIcon status={false} />
                    <span className="text-sm text-muted-foreground">
                      Stripe metrics disabled (local dev or missing config)
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">MRR</p>
                        <p className="text-2xl font-bold">
                          ${((stripeMetrics.mrr_cents || 0) / 100).toFixed(2)}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Active LTO</p>
                        <p className="text-2xl font-bold">
                          {stripeMetrics.active_lto_count || 0}
                          <span className="text-sm font-normal text-muted-foreground ml-1">
                            subscriptions
                          </span>
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">24h Failures</p>
                        <p className="text-2xl font-bold">
                          {stripeMetrics.failures_24h || 0}
                          <span className="text-sm font-normal text-muted-foreground ml-1">
                            errors
                          </span>
                        </p>
                      </div>
                    </div>
                    <Separator />
                    <Button
                      onClick={handleRefreshMetrics}
                      disabled={loadingMetrics}
                      variant="outline"
                      size="sm"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${loadingMetrics ? 'animate-spin' : ''}`} />
                      {loadingMetrics ? 'Refreshing...' : 'Refresh Metrics'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Environment Variables */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Environment Variables
                </CardTitle>
                <CardDescription>
                  Critical configuration status (values are masked)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {Object.entries(data.env).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between py-2 border-b last:border-0"
                    >
                      <span className="text-sm font-mono">{key}</span>
                      <div className="flex items-center gap-2">
                        <StatusIcon status={value} />
                        <Badge variant={value ? 'default' : 'secondary'}>
                          {value ? 'Set' : 'Missing'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Build Information */}
            <Card>
              <CardHeader>
                <CardTitle>Build Information</CardTitle>
                <CardDescription>Current deployment details</CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Environment</dt>
                    <dd className="font-mono">
                      {data.vercel?.enabled ? 'Production' : 'Development'}
                    </dd>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Commit SHA</dt>
                    <dd className="font-mono">{data.github?.sha || 'Unknown'}</dd>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Deployment ID</dt>
                    <dd className="font-mono">{data.vercel?.id || 'Unknown'}</dd>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Deployment URL</dt>
                    <dd className="font-mono truncate max-w-xs">
                      {data.vercel?.url || 'Not available'}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Links</CardTitle>
                <CardDescription>External monitoring and deployment tools</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  <a
                    href="https://vercel.com/ucgr8s-projects/attaindomains/logs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                  >
                    Vercel Logs
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                  <a
                    href="https://vercel.com/ucgr8s-projects/attaindomains/deployments"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                  >
                    Latest Deployment
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                  <a
                    href="https://domains-lb.sentry.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                  >
                    Sentry Dashboard
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                  <a
                    href={data.links.health_all}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                  >
                    Raw Health Data
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Raw JSON */}
            <Card>
              <CardHeader>
                <CardTitle>Raw Ops JSON</CardTitle>
                <CardDescription>Complete system data in JSON format</CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-muted p-4 rounded-lg overflow-auto max-h-[400px] text-xs font-mono">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </CardContent>
            </Card>

            {/* Footer */}
            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                Raw JSON data available at:{' '}
                <a
                  href="/api/health/all"
                  className="text-primary hover:underline font-mono"
                  target="_blank"
                >
                  /api/health/all
                </a>
              </p>
              <p>
                This page is public, read-only, and masks all secrets. No authentication required.
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}
